import React from 'react';
/**
 * Note: Original implementation copied from https://github.com/scottrippey/react-use-event-hook/blob/75ba34af9175dc311afb3fb302d6fea44e4a5203/src/useEvent.ts
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

type AnyFunction = (...args: any[]) => any;

/**
 * Similar to useCallback, with a few subtle differences:
 * - The returned function is a stable reference, and will always be the same between renders
 * - No dependency lists required
 * - Properties or state accessed within the callback will always be "current"
 */
export function useEvent<TCallback extends AnyFunction>(
  callback: TCallback,
): TCallback {
  // Keep track of the latest callback:
  const latestRef = React.useRef<TCallback>(
    useEvent_shouldNotBeInvokedBeforeMount as any,
  );

  React.useInsertionEffect(() => {
    latestRef.current = callback;
  }, [callback]);

  // Create a stable callback that always calls the latest callback:
  // using useRef instead of useCallback avoids creating and empty array on every render
  const stableRef = React.useRef<TCallback>(null as any);

  if (!stableRef.current) {
    stableRef.current = function (this: any) {
      // eslint-disable-next-line prefer-rest-params
      return latestRef.current.apply(this, arguments as any);
    } as TCallback;
  }

  return stableRef.current;
}

export function useEventWithWrapper<
  CallbackParams extends unknown[],
  CallbackReturn,
  WrapperReturn,
>(
  wrapper: (
    fn: (...args: CallbackParams) => CallbackReturn,
  ) => (...args: CallbackParams) => WrapperReturn,
  callback: (...args: CallbackParams) => CallbackReturn,
): (...args: CallbackParams) => WrapperReturn {
  return useEvent(wrapper(callback));
}

/**
 * Render methods should be pure, especially when concurrency is used,
 * so we will throw this error if the callback is called while rendering.
 */
function useEvent_shouldNotBeInvokedBeforeMount() {
  throw new Error(
    'INVALID_USEEVENT_INVOCATION: the callback from useEvent cannot be invoked before the component has mounted.',
  );
}

export default useEvent;
